import { Link } from "react-router-dom";
import { ArrowRight, Shield, Clock, Users, Scale } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { FadeIn, StaggerContainer, StaggerItem } from "@/components/FadeIn";
import { SectionHeading, GoldDivider, GoldDividerLeft } from "@/components/SectionHeading";

const industries = [
    { name: "Elektronik & Software", icon: "01" },
    { name: "Bau und Immobilien", icon: "02" },
    { name: "Handel & Konsumgüter", icon: "03" },
    { name: "Finanzdienstleistungen", icon: "04" },
    { name: "Energiewirtschaft", icon: "05" },
    { name: "Family Office", icon: "06" },
];

const values = [
    {
        icon: Shield,
        title: "Vertrauen",
        description: "Diskrete und vertrauliche Beratung für alle Ihre steuerlichen Angelegenheiten.",
    },
    {
        icon: Clock,
        title: "Zeit",
        description: "Wir nehmen uns die Zeit, Ihre individuelle Situation vollständig zu verstehen.",
    },
    {
        icon: Users,
        title: "Partnerschaft",
        description: "Langfristige Mandantenbeziehungen basierend auf gegenseitigem Respekt.",
    },
    {
        icon: Scale,
        title: "Kompetenz",
        description: "Fundiertes Fachwissen und kontinuierliche Weiterbildung.",
    },
];

export default function Home() {
    return (
        <div className="bg-background">
            {/* Hero Section */}
            <section className="relative min-h-screen flex items-center pt-24 lg:pt-0">
                {/* Full-width background image */}
                <div className="absolute inset-0">
                    <img
                        src="/images/Neuhauser_Str._27_Augustinerbraeu_Muenchen-1.jpg"
                        alt="Augustinerbräu München"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-background/90 via-background/70 to-background/40" />
                    <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-background/50" />
                </div>

                {/* Subtle decorative elements */}
                <div className="absolute inset-0 overflow-hidden">
                    <div className="absolute top-1/4 right-0 w-px h-32 gold-line-vertical opacity-30" />
                    <div className="absolute bottom-1/3 left-1/4 w-24 h-px gold-line opacity-20" />
                </div>

                <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-32 relative z-10">
                    <div className="grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
                        <div className="lg:col-span-7">
                            <FadeIn delay={0.1}>
                                <span className="font-sans text-xs tracking-wide-premium uppercase text-gold mb-6 block">
                                    Steuerberatung seit 2006
                                </span>
                            </FadeIn>
                            
                            <FadeIn delay={0.2}>
                                <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl xl:text-7xl text-foreground leading-tight mb-8">
                                    Steuerberatung<br />
                                    <span className="text-gold">Mario Ruberto</span>
                                </h1>
                            </FadeIn>

                            <FadeIn delay={0.3}>
                                <GoldDividerLeft className="mb-8" />
                            </FadeIn>

                            <FadeIn delay={0.4}>
                                <p className="font-sans text-base md:text-lg text-muted-foreground leading-relaxed max-w-xl mb-10">
                                    Kompetente und diskrete Steuerberatung für Unternehmer, 
                                    Freiberufler und Privatpersonen in München. Wir nehmen 
                                    uns Zeit für Ihre individuellen Anliegen.
                                </p>
                            </FadeIn>

                            <FadeIn delay={0.5}>
                                <div className="flex flex-col sm:flex-row gap-4">
                                    <Button
                                        asChild
                                        className="bg-transparent border border-gold text-gold hover:bg-gold hover:text-primary-foreground transition-all duration-400 px-8 py-6 font-sans text-sm tracking-premium uppercase"
                                    >
                                        <Link to="/kontakt">
                                            Kontakt aufnehmen
                                            <ArrowRight className="ml-2 h-4 w-4" />
                                        </Link>
                                    </Button>
                                    <Button
                                        asChild
                                        variant="ghost"
                                        className="text-muted-foreground hover:text-foreground hover:bg-transparent transition-colors duration-300 px-8 py-6 font-sans text-sm tracking-premium uppercase"
                                    >
                                        <Link to="/leistungen">
                                            Unsere Leistungen
                                        </Link>
                                    </Button>
                                </div>
                            </FadeIn>
                        </div>

                        <div className="lg:col-span-5 hidden lg:block">
                            <FadeIn delay={0.6} direction="left">
                                <div className="relative">
                                    <div className="w-full h-[520px] rounded-lg" />
                                    <Card className="absolute bottom-8 left-8 right-8 bg-card/90 backdrop-blur-sm border-border p-10">
                                        <div className="space-y-6">
                                            <div>
                                                <span className="font-sans text-xs tracking-premium uppercase text-gold">
                                                    Erfahrung
                                                </span>
                                                <p className="font-serif text-4xl text-foreground mt-2">
                                                    20 Jahre
                                                </p>
                                            </div>
                                            <Separator className="bg-border" />
                                            <div>
                                                <span className="font-sans text-xs tracking-premium uppercase text-gold">
                                                    Standort
                                                </span>
                                                <p className="font-serif text-2xl text-foreground mt-2">
                                                    München
                                                </p>
                                            </div>
                                            <Separator className="bg-border" />
                                            <div>
                                                <span className="font-sans text-xs tracking-premium uppercase text-gold">
                                                    Betreuung
                                                </span>
                                                <p className="font-serif text-2xl text-foreground mt-2">
                                                    Persönlich
                                                </p>
                                            </div>
                                        </div>
                                    </Card>
                                </div>
                            </FadeIn>
                        </div>
                    </div>
                </div>
            </section>

            {/* Values Section */}
            <section className="py-24 lg:py-32 bg-charcoal-mid">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <FadeIn>
                        <SectionHeading
                            eyebrow="Unsere Werte"
                            title="Diskretion, Kompetenz und persönliche Betreuung"
                            description="Ein guter Steuerberater sollte sich vor allem eines für Sie nehmen: Zeit. An diesem Anspruch dürfen Sie jede Leistung messen, die wir für Sie erbringen."
                            alignment="center"
                            className="mb-16 lg:mb-20"
                        />
                    </FadeIn>

                    <StaggerContainer className="grid md:grid-cols-2 lg:grid-cols-4 gap-8" staggerDelay={0.1}>
                        {values.map((value, index) => (
                            <StaggerItem key={value.title}>
                                <Card className="bg-card border-border p-8 h-full hover:border-gold/30 transition-colors duration-400">
                                    <div className="flex flex-col h-full">
                                        <value.icon className="h-6 w-6 text-gold mb-6" strokeWidth={1.5} />
                                        <h3 className="font-serif text-xl text-foreground mb-4">
                                            {value.title}
                                        </h3>
                                        <p className="font-sans text-sm text-muted-foreground leading-relaxed">
                                            {value.description}
                                        </p>
                                    </div>
                                </Card>
                            </StaggerItem>
                        ))}
                    </StaggerContainer>
                </div>
            </section>

            {/* Industries Section */}
            <section className="py-24 lg:py-32 bg-background">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-start">
                        <div>
                            <FadeIn>
                                <SectionHeading
                                    eyebrow="Branchen"
                                    title="Vielfältige Expertise für unterschiedliche Mandanten"
                                    description="Unser Kundenportfolio umfasst alle Branchen – vom Handwerk bis zur Managementetage, von Privatpersonen bis zu Family Offices."
                                />
                            </FadeIn>
                            
                            <FadeIn delay={0.2}>
                                <div className="mt-10">
                                    <Button
                                        asChild
                                        variant="ghost"
                                        className="text-gold hover:text-foreground hover:bg-transparent transition-colors duration-300 px-0 font-sans text-sm tracking-premium uppercase"
                                    >
                                        <Link to="/leistungen">
                                            Alle Leistungen entdecken
                                            <ArrowRight className="ml-2 h-4 w-4" />
                                        </Link>
                                    </Button>
                                </div>
                            </FadeIn>
                        </div>

                        <div>
                            <StaggerContainer className="grid grid-cols-2 gap-6" staggerDelay={0.08}>
                                {industries.map((industry) => (
                                    <StaggerItem key={industry.name}>
                                        <div className="group py-6 border-b border-border hover:border-gold/50 transition-colors duration-400">
                                            <span className="font-sans text-xs text-gold mb-2 block">
                                                {industry.icon}
                                            </span>
                                            <span className="font-sans text-sm text-foreground group-hover:text-gold transition-colors duration-300">
                                                {industry.name}
                                            </span>
                                        </div>
                                    </StaggerItem>
                                ))}
                            </StaggerContainer>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="relative py-24 lg:py-32 border-t border-b border-border overflow-hidden">
                {/* Background Image */}
                <div className="absolute inset-0">
                    <img
                        src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1920&q=80&auto=format&fit=crop"
                        alt="Beratungsgespräch"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-charcoal-mid/85" />
                </div>
                
                <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
                    <div className="max-w-3xl mx-auto text-center">
                        <FadeIn>
                            <GoldDivider className="mb-10" />
                        </FadeIn>
                        
                        <FadeIn delay={0.1}>
                            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-foreground mb-6">
                                Vereinbaren Sie ein persönliches Beratungsgespräch
                            </h2>
                        </FadeIn>

                        <FadeIn delay={0.2}>
                            <p className="font-sans text-base md:text-lg text-muted-foreground mb-10 max-w-xl mx-auto">
                                Wir freuen uns darauf, Sie kennenzulernen und Ihre 
                                steuerlichen Anliegen gemeinsam zu besprechen.
                            </p>
                        </FadeIn>

                        <FadeIn delay={0.3}>
                            <Button
                                asChild
                                className="bg-gold text-primary-foreground hover:bg-gold-muted transition-all duration-400 px-10 py-6 font-sans text-sm tracking-premium uppercase"
                            >
                                <Link to="/kontakt">
                                    Termin vereinbaren
                                </Link>
                            </Button>
                        </FadeIn>
                    </div>
                </div>
            </section>
        </div>
    );
}
